import { useState, useMemo } from 'react';
import Swal from 'sweetalert2';
import type { SocialPlatformData, SocialConnection, StatusFilter } from '@/mocks/superAdminSocialStatus';
import { statusMeta, statusFilterKeys, filterMeta, getFilteredConnections } from '@/mocks/superAdminSocialStatus';

interface Props {
  platform: SocialPlatformData;
  onClose: () => void;
}

export default function RadioListModal({ platform, onClose }: Props) {
  const [activeFilter, setActiveFilter] = useState<StatusFilter>('all');

  const filtered = useMemo(() => getFilteredConnections(platform, activeFilter), [platform, activeFilter]);

  const counts = useMemo(() => {
    const c: Record<StatusFilter, number> = { all: 0, connected: 0, error: 0, pending: 0, expired: 0 };
    platform.connections.forEach((conn) => {
      c.all++;
      c[conn.status]++;
    });
    return c;
  }, [platform]);

  const handleRenew = (conn: SocialConnection) => {
    Swal.fire({
      icon: 'success',
      title: 'Conexión renovada',
      html: `<p class="text-xs text-left"><b>${conn.radioName}</b> — ${platform.label}</p><p class="text-xs text-left mt-1 text-foreground-500">La conexión se renovó correctamente.</p>`,
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      customClass: { popup: 'rounded-2xl text-sm' },
    });
  };

  const handleReconnect = (conn: SocialConnection) => {
    Swal.fire({
      icon: 'success',
      title: 'Reconexión exitosa',
      html: `<p class="text-xs text-left"><b>${conn.radioName}</b> — ${platform.label}</p><p class="text-xs text-left mt-1 text-foreground-500">La cuenta se reconectó correctamente.</p>`,
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      customClass: { popup: 'rounded-2xl text-sm' },
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden border border-background-200 flex flex-col">
        {/* Header */}
        <div className="shrink-0 bg-background-50 rounded-t-2xl border-b border-background-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white"
              style={{ backgroundColor: platform.color }}
            >
              <i className={`${platform.icon} text-base`}></i>
            </div>
            <div>
              <h3 className="text-foreground-900 font-bold text-sm">{platform.label} · Radios Vinculadas</h3>
              <p className="text-foreground-400 text-[10px]">{platform.connections.length} radios en total</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
          >
            <i className="ri-close-line text-foreground-500 text-sm"></i>
          </button>
        </div>

        {/* Filters */}
        <div className="shrink-0 px-6 py-3 border-b border-background-100 flex items-center gap-2 flex-wrap">
          {statusFilterKeys.map((key) => {
            const meta = filterMeta[key];
            const isActive = activeFilter === key;
            return (
              <button
                key={key}
                onClick={() => setActiveFilter(key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  isActive
                    ? 'bg-foreground-950 text-background-50'
                    : 'bg-background-50 border border-background-200 text-foreground-500 hover:bg-background-100'
                }`}
              >
                <i className={`${meta.icon} text-xs`}></i>
                {meta.label}
                <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-md text-[9px] font-bold ${
                  isActive ? 'bg-background-50/20 text-background-50' : 'bg-background-100 text-foreground-400'
                }`}>
                  {counts[key]}
                </span>
              </button>
            );
          })}
        </div>

        {/* Radio list */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
          {filtered.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-12 h-12 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-3">
                <i className="ri-search-line text-foreground-400 text-lg"></i>
              </div>
              <p className="text-foreground-500 text-xs font-medium">Sin resultados</p>
              <p className="text-foreground-400 text-[10px] mt-0.5">No hay radios con este estado</p>
            </div>
          ) : (
            filtered.map((conn) => {
              const meta = statusMeta[conn.status];
              return (
                <div
                  key={conn.radioId}
                  className="bg-background-100 rounded-xl p-3.5 flex items-center gap-3 hover:bg-background-200/70 transition-colors"
                >
                  {/* Status dot */}
                  <div className={`w-3 h-3 rounded-full ${meta.dot} shrink-0`}></div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-foreground-800 text-xs font-semibold">{conn.radioName}</p>
                      <span className="px-1.5 py-0.5 rounded-md bg-background-200 text-foreground-500 text-[9px] font-medium">
                        {conn.plan}
                      </span>
                    </div>
                    <p className="text-foreground-400 text-[10px] mt-0.5">
                      Última actividad: {new Date(conn.lastActivity).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>

                  {/* Status badge */}
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold ${meta.bg} ${meta.text}`}>
                    {meta.label}
                  </span>

                  {/* Quick actions */}
                  {(conn.status === 'expired' || conn.status === 'error' || conn.status === 'pending') && (
                    <div className="flex items-center gap-1.5 shrink-0">
                      {conn.status === 'expired' && (
                        <button
                          onClick={() => handleRenew(conn)}
                          className="px-2.5 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-[10px] font-semibold hover:bg-emerald-100 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          Renovar
                        </button>
                      )}
                      {(conn.status === 'error' || conn.status === 'pending') && (
                        <button
                          onClick={() => handleReconnect(conn)}
                          className="px-2.5 py-1.5 rounded-lg bg-amber-50 text-amber-700 text-[10px] font-semibold hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          Reconectar
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-background-200 px-6 py-3 flex items-center justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-[11px] font-semibold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}